// NOTE: NSAI-7215 — the expression language of the ```ui contract.
//
// A spec's formulas are written by a model and arrive inside an answer, so
// they are data, not code. Nothing here calls `eval`, `Function` or any
// property lookup on a JS object: identifiers resolve through a Map the caller
// builds from the spec's own inputs and values, and function names resolve
// through a fixed Map of pure math helpers. A name that is neither is an error
// at parse time — `constructor`, `__proto__` or `globalThis` are just unknown
// identifiers here.
//
// The grammar, lowest precedence first:
//   conditional  := or ( "?" conditional ":" conditional )?
//   or           := and ( "||" and )*
//   and          := equality ( "&&" equality )*
//   equality     := comparison ( ( "==" | "!=" ) comparison )*
//   comparison   := additive ( ( "<" | "<=" | ">" | ">=" ) additive )*
//   additive     := multiplicative ( ( "+" | "-" ) multiplicative )*
//   multiplicative := unary ( ( "*" | "/" | "%" ) unary )*
//   unary        := ( "-" | "+" | "!" ) unary | power
//   power        := primary ( "^" unary )?
//   primary      := number | "true" | "false" | identifier
//                 | identifier "(" args ")" | "(" conditional ")"
//
// `^` binds tighter than a leading minus, so `-2^2` is -4 as in maths.
// Comparisons do not chain: `a < b < c` is `(a < b) < c`, a true/false read as
// 1/0 — a range check is written `a < b && b < c`.

import {
  MAX_UI_EXPRESSION_DEPTH,
  MAX_UI_EXPRESSION_LENGTH,
  MAX_UI_EXPRESSION_NODES,
} from "../../constants/ui";
import {
  EUiExpressionNodeKind,
  type IEvaluateUiExpressionProps,
  type TParseUiExpressionResult,
  type TUiBinaryOperator,
  type TUiExpressionNode,
  type TUiScalar,
} from "../../types/ui";

enum ETokenKind {
  NUMBER = "number",
  IDENTIFIER = "identifier",
  OPERATOR = "operator",
  END = "end",
}

interface IToken {
  kind: ETokenKind;
  text: string;
  position: number;
}

interface IUiFunction {
  minArgs: number;
  maxArgs: number;
  apply: (args: number[]) => number;
}

const roundTo = (args: number[]): number => {
  const [value, digits = 0] = args;
  const factor = 10 ** Math.max(0, Math.min(10, Math.trunc(digits)));

  return Math.round(value * factor) / factor;
};

// NOTE: The whole callable surface of the language. Every entry is a pure
// function of numbers; adding one is a deliberate, reviewed change.
const UI_EXPRESSION_FUNCTIONS: ReadonlyMap<string, IUiFunction> = new Map([
  ["min", { minArgs: 1, maxArgs: 8, apply: (args) => Math.min(...args) }],
  ["max", { minArgs: 1, maxArgs: 8, apply: (args) => Math.max(...args) }],
  ["round", { minArgs: 1, maxArgs: 2, apply: roundTo }],
  ["floor", { minArgs: 1, maxArgs: 1, apply: (args) => Math.floor(args[0]) }],
  ["ceil", { minArgs: 1, maxArgs: 1, apply: (args) => Math.ceil(args[0]) }],
  ["abs", { minArgs: 1, maxArgs: 1, apply: (args) => Math.abs(args[0]) }],
  ["sqrt", { minArgs: 1, maxArgs: 1, apply: (args) => Math.sqrt(args[0]) }],
  ["log", { minArgs: 1, maxArgs: 1, apply: (args) => Math.log(args[0]) }],
  ["log10", { minArgs: 1, maxArgs: 1, apply: (args) => Math.log10(args[0]) }],
  ["exp", { minArgs: 1, maxArgs: 1, apply: (args) => Math.exp(args[0]) }],
  ["pow", { minArgs: 2, maxArgs: 2, apply: (args) => args[0] ** args[1] }],
  [
    "clamp",
    {
      minArgs: 3,
      maxArgs: 3,
      apply: (args) => Math.min(Math.max(args[0], args[1]), args[2]),
    },
  ],
]);

export const UI_EXPRESSION_FUNCTION_NAMES: readonly string[] = [
  ...UI_EXPRESSION_FUNCTIONS.keys(),
];

// NOTE: Longest first, so `<=` is read before `<`.
const OPERATORS: readonly string[] = [
  "==",
  "!=",
  "<=",
  ">=",
  "&&",
  "||",
  "+",
  "-",
  "*",
  "/",
  "%",
  "^",
  "<",
  ">",
  "!",
  "?",
  ":",
  "(",
  ")",
  ",",
];

const NUMBER_PATTERN = /^(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/;
const IDENTIFIER_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*/;
const WHITESPACE_PATTERN = /^\s+/;

class UiExpressionError extends Error {}

const tokenize = (source: string): IToken[] => {
  const tokens: IToken[] = [];
  let position = 0;

  while (position < source.length) {
    const rest = source.slice(position);
    const whitespace = WHITESPACE_PATTERN.exec(rest);

    if (whitespace) {
      position += whitespace[0].length;
      continue;
    }

    const numberMatch = NUMBER_PATTERN.exec(rest);

    if (numberMatch) {
      tokens.push({
        kind: ETokenKind.NUMBER,
        text: numberMatch[0],
        position,
      });
      position += numberMatch[0].length;
      continue;
    }

    const identifierMatch = IDENTIFIER_PATTERN.exec(rest);

    if (identifierMatch) {
      tokens.push({
        kind: ETokenKind.IDENTIFIER,
        text: identifierMatch[0],
        position,
      });
      position += identifierMatch[0].length;
      continue;
    }

    const operator = OPERATORS.find((candidate) => rest.startsWith(candidate));

    if (operator === undefined) {
      throw new UiExpressionError(
        `Unexpected character "${rest[0]}" at position ${position + 1} — formulas use numbers, ids, + - * / % ^, comparisons, && || !, ? : and the listed functions`,
      );
    }

    tokens.push({ kind: ETokenKind.OPERATOR, text: operator, position });
    position += operator.length;
  }

  tokens.push({ kind: ETokenKind.END, text: "", position });

  return tokens;
};

// NOTE: A small recursive-descent parser. It counts every node it builds and
// how deep it has nested, and refuses past either bound, so no formula can cost
// more than a fixed amount of work however it is written.
class Parser {
  private index = 0;
  private nodeCount = 0;
  private depth = 0;
  private readonly identifiers = new Set<string>();

  constructor(private readonly tokens: IToken[]) {}

  parse(): { node: TUiExpressionNode; identifiers: string[] } {
    const node = this.parseConditional();
    const trailing = this.peek();

    if (trailing.kind !== ETokenKind.END) {
      throw new UiExpressionError(
        `Unexpected "${trailing.text}" at position ${trailing.position + 1} — an operator is probably missing`,
      );
    }

    return { node, identifiers: [...this.identifiers] };
  }

  private peek(): IToken {
    return this.tokens[this.index];
  }

  private advance(): IToken {
    const token = this.tokens[this.index];
    this.index += 1;

    return token;
  }

  private isOperator(text: string): boolean {
    const token = this.peek();

    return token.kind === ETokenKind.OPERATOR && token.text === text;
  }

  private expectOperator(text: string): void {
    const token = this.advance();

    if (token.kind !== ETokenKind.OPERATOR || token.text !== text) {
      throw new UiExpressionError(
        token.kind === ETokenKind.END
          ? `The formula ends early — "${text}" is missing`
          : `Expected "${text}" at position ${token.position + 1}, found "${token.text}"`,
      );
    }
  }

  private count<TNode extends TUiExpressionNode>(node: TNode): TNode {
    this.nodeCount += 1;

    if (this.nodeCount > MAX_UI_EXPRESSION_NODES) {
      throw new UiExpressionError(
        `The formula is too complex (more than ${MAX_UI_EXPRESSION_NODES} parts) — split it into named values`,
      );
    }

    return node;
  }

  private enter(): void {
    this.depth += 1;

    if (this.depth > MAX_UI_EXPRESSION_DEPTH) {
      throw new UiExpressionError(
        `The formula nests more than ${MAX_UI_EXPRESSION_DEPTH} levels deep — split it into named values`,
      );
    }
  }

  private leave(): void {
    this.depth -= 1;
  }

  private parseConditional(): TUiExpressionNode {
    this.enter();
    const condition = this.parseBinaryLevel(0);

    if (!this.isOperator("?")) {
      this.leave();
      return condition;
    }

    this.advance();
    const whenTrue = this.parseConditional();
    this.expectOperator(":");
    const whenFalse = this.parseConditional();
    this.leave();

    return this.count({
      kind: EUiExpressionNodeKind.CONDITIONAL,
      condition,
      whenTrue,
      whenFalse,
    });
  }

  // NOTE: One loop for the six left-associative binary levels, lowest first.
  private parseBinaryLevel(level: number): TUiExpressionNode {
    if (level >= BINARY_LEVELS.length) {
      return this.parseUnary();
    }

    const operators = BINARY_LEVELS[level];
    let left = this.parseBinaryLevel(level + 1);

    while (true) {
      const token = this.peek();
      const operator = operators.find(
        (candidate) =>
          token.kind === ETokenKind.OPERATOR && token.text === candidate,
      );

      if (operator === undefined) {
        return left;
      }

      this.advance();
      const right = this.parseBinaryLevel(level + 1);
      left = this.count({
        kind: EUiExpressionNodeKind.BINARY,
        operator,
        left,
        right,
      });
    }
  }

  private parseUnary(): TUiExpressionNode {
    const token = this.peek();

    if (
      token.kind === ETokenKind.OPERATOR &&
      (token.text === "-" || token.text === "+" || token.text === "!")
    ) {
      this.advance();
      this.enter();
      const operand = this.parseUnary();
      this.leave();

      return this.count({
        kind: EUiExpressionNodeKind.UNARY,
        operator: token.text,
        operand,
      });
    }

    return this.parsePower();
  }

  private parsePower(): TUiExpressionNode {
    const base = this.parsePrimary();

    if (!this.isOperator("^")) {
      return base;
    }

    this.advance();
    this.enter();
    const exponent = this.parseUnary();
    this.leave();

    return this.count({
      kind: EUiExpressionNodeKind.BINARY,
      operator: "^",
      left: base,
      right: exponent,
    });
  }

  private parsePrimary(): TUiExpressionNode {
    const token = this.advance();

    if (token.kind === ETokenKind.NUMBER) {
      const value = Number(token.text);

      if (!Number.isFinite(value)) {
        throw new UiExpressionError(`The number "${token.text}" is too large`);
      }

      return this.count({ kind: EUiExpressionNodeKind.NUMBER, value });
    }

    if (token.kind === ETokenKind.IDENTIFIER) {
      if (token.text === "true" || token.text === "false") {
        return this.count({
          kind: EUiExpressionNodeKind.BOOLEAN,
          value: token.text === "true",
        });
      }

      if (this.isOperator("(")) {
        return this.parseCall(token);
      }

      this.identifiers.add(token.text);

      return this.count({
        kind: EUiExpressionNodeKind.IDENTIFIER,
        name: token.text,
      });
    }

    if (token.kind === ETokenKind.OPERATOR && token.text === "(") {
      const inner = this.parseConditional();
      this.expectOperator(")");

      return inner;
    }

    if (token.kind === ETokenKind.END) {
      throw new UiExpressionError("The formula ends where a value is expected");
    }

    throw new UiExpressionError(
      `Unexpected "${token.text}" at position ${token.position + 1} — a number, an id or "(" is expected here`,
    );
  }

  private parseCall(nameToken: IToken): TUiExpressionNode {
    const definition = UI_EXPRESSION_FUNCTIONS.get(nameToken.text);

    if (definition === undefined) {
      throw new UiExpressionError(
        `Unknown function "${nameToken.text}" — use one of ${UI_EXPRESSION_FUNCTION_NAMES.join(", ")}`,
      );
    }

    this.expectOperator("(");
    this.enter();
    const args: TUiExpressionNode[] = [];

    if (!this.isOperator(")")) {
      args.push(this.parseConditional());

      while (this.isOperator(",")) {
        this.advance();
        args.push(this.parseConditional());
      }
    }

    this.expectOperator(")");
    this.leave();

    if (args.length < definition.minArgs || args.length > definition.maxArgs) {
      const expected =
        definition.minArgs === definition.maxArgs
          ? `${definition.minArgs}`
          : `${definition.minArgs}–${definition.maxArgs}`;

      throw new UiExpressionError(
        `${nameToken.text}() takes ${expected} argument(s), got ${args.length}`,
      );
    }

    return this.count({
      kind: EUiExpressionNodeKind.CALL,
      name: nameToken.text,
      args,
    });
  }
}

const BINARY_LEVELS: readonly (readonly TUiBinaryOperator[])[] = [
  ["||"],
  ["&&"],
  ["==", "!="],
  ["<", "<=", ">", ">="],
  ["+", "-"],
  ["*", "/", "%"],
];

export const parseUiExpression = (source: string): TParseUiExpressionResult => {
  if (source.trim().length === 0) {
    return { success: false, error: "The formula is empty" };
  }

  if (source.length > MAX_UI_EXPRESSION_LENGTH) {
    return {
      success: false,
      error: `A formula is at most ${MAX_UI_EXPRESSION_LENGTH} characters — move part of it into a named value`,
    };
  }

  try {
    const parsed = new Parser(tokenize(source)).parse();

    return { success: true, ...parsed };
  } catch (error) {
    if (error instanceof UiExpressionError) {
      return { success: false, error: error.message };
    }

    throw error;
  }
};

const toNumber = (value: TUiScalar): number =>
  typeof value === "boolean" ? Number(value) : value;

const toBoolean = (value: TUiScalar): boolean =>
  typeof value === "boolean" ? value : value !== 0 && !Number.isNaN(value);

interface IApplyBinaryProps {
  operator: TUiBinaryOperator;
  left: TUiScalar;
  right: TUiScalar;
}

const applyBinary = ({
  operator,
  left,
  right,
}: IApplyBinaryProps): TUiScalar => {
  switch (operator) {
    case "+":
      return toNumber(left) + toNumber(right);
    case "-":
      return toNumber(left) - toNumber(right);
    case "*":
      return toNumber(left) * toNumber(right);
    case "/":
      return toNumber(left) / toNumber(right);
    case "%":
      return toNumber(left) % toNumber(right);
    case "^":
      return toNumber(left) ** toNumber(right);
    case "==":
      return toNumber(left) === toNumber(right);
    case "!=":
      return toNumber(left) !== toNumber(right);
    case "<":
      return toNumber(left) < toNumber(right);
    case "<=":
      return toNumber(left) <= toNumber(right);
    case ">":
      return toNumber(left) > toNumber(right);
    case ">=":
      return toNumber(left) >= toNumber(right);
    // NOTE: `&&` and `||` never reach here — `evaluateUiExpression` handles
    // them first so the right side is evaluated only when it matters.
    default:
      return Number.NaN;
  }
};

// NOTE: Evaluates a parsed formula against a scope. An identifier the scope
// does not hold evaluates to NaN rather than throwing: the schema has already
// checked every reference, and NaN renders as "—" instead of taking down the
// message.
export const evaluateUiExpression = ({
  node,
  scope,
}: IEvaluateUiExpressionProps): TUiScalar => {
  switch (node.kind) {
    case EUiExpressionNodeKind.NUMBER:
    case EUiExpressionNodeKind.BOOLEAN:
      return node.value;
    case EUiExpressionNodeKind.IDENTIFIER:
      return scope.get(node.name) ?? Number.NaN;
    case EUiExpressionNodeKind.UNARY: {
      const operand = evaluateUiExpression({ node: node.operand, scope });

      if (node.operator === "!") {
        return !toBoolean(operand);
      }

      return node.operator === "-" ? -toNumber(operand) : toNumber(operand);
    }
    case EUiExpressionNodeKind.BINARY: {
      // NOTE: && and || short-circuit, so `rate > 0 && total / rate > 1`
      // never divides by zero on the way to false.
      if (node.operator === "&&" || node.operator === "||") {
        const isLeftTrue = toBoolean(
          evaluateUiExpression({ node: node.left, scope }),
        );

        if (node.operator === "&&" ? !isLeftTrue : isLeftTrue) {
          return isLeftTrue;
        }

        return toBoolean(evaluateUiExpression({ node: node.right, scope }));
      }

      return applyBinary({
        operator: node.operator,
        left: evaluateUiExpression({ node: node.left, scope }),
        right: evaluateUiExpression({ node: node.right, scope }),
      });
    }
    case EUiExpressionNodeKind.CONDITIONAL:
      return toBoolean(evaluateUiExpression({ node: node.condition, scope }))
        ? evaluateUiExpression({ node: node.whenTrue, scope })
        : evaluateUiExpression({ node: node.whenFalse, scope });
    case EUiExpressionNodeKind.CALL: {
      const definition = UI_EXPRESSION_FUNCTIONS.get(node.name);

      if (definition === undefined) {
        return Number.NaN;
      }

      return definition.apply(
        node.args.map((arg) =>
          toNumber(evaluateUiExpression({ node: arg, scope })),
        ),
      );
    }
    default:
      return Number.NaN;
  }
};

export const toUiNumber = toNumber;
